import { SessionConfig } from "@acin.reactjs/acin-ui/baseApp";
import { addMinutes, addSeconds } from "date-fns";
import {
  AppBackendAPI,
  AppBackendProviderProps,
  LoginResponse,
} from "../../interfaces/BackendInterfaces";
import { AppParamsProps, Session } from "../../interfaces/BaseAppInterfaces";

type AppSessionConfig = SessionConfig<
  AppParamsProps,
  Session,
  AppBackendAPI,
  AppBackendProviderProps
>;

const authToken: {
  current:
    | {
        token: string;
        generationDate: Date;
      }
    | undefined;
} = {
  current: undefined,
};

/**
 * Flag to indicate the session has expired and refresh has failed.
 * Prevents further requests from being sent without auth.
 */
const sessionState = {
  expired: false,
};

export const isSessionExpired = () => sessionState.expired;

export const setSessionExpired = () => {
  sessionState.expired = true;
};

export const resetSessionExpired = () => {
  sessionState.expired = false;
};

const onLogout: AppSessionConfig["onLogout"] = (_1, { api }) => {
  Object.keys(sessionStorage).forEach((key) => {
    if (key.startsWith("acinTabs.")) {
      sessionStorage.removeItem(key);
    }
  });
  sessionStorage.setItem("closeTabs", "true");
  localStorage.removeItem("closedNotifications");

  if (authToken.current && !isSessionExpired()) {
    void api
      .post("logout", undefined, {
        noLoading: true,
        doNotTriggerSessionCheck: true,
        dontTriggerException: true,
        dontTriggerUnauthorizedException: true,
        authenticationToken: authToken.current.token,
      })
      .promise.finally(() => {
        authToken.current = undefined;
      });
  } else {
    authToken.current = undefined;
  }

  // Reset the expired flag so next login works correctly
  resetSessionExpired();
  return undefined;
};

const onSessionCheck: AppSessionConfig["onSessionCheck"] = async (
  lastTimeCheck,
  timeout,
  session,
  context,
) => {
  if (session) {
    const parsedTimeout = timeout || Number(session.auth.expiresIn);

    if (addMinutes(lastTimeCheck, parsedTimeout) < new Date()) {
      return Promise.resolve(false);
    }

    return Boolean(await checkSessionToken(context));
  }

  return Promise.resolve(false);
};

const sessionConfig: AppSessionConfig = {
  onLogout,
  onSessionCheck,
};

export default sessionConfig;

/**
 *
 * @param session this will generate a new session token if the current one is expired
 */
export const checkSessionToken = async ({
  state: { session },
  api,
  methods: { changeSession },
}: Parameters<NonNullable<AppSessionConfig["onSessionCheck"]>>[3]): Promise<
  false | string
> => {
  const publicRoutes = ["select-entity", "login"];

  const currentPath = window.location.pathname;
  const isPublicRoute = publicRoutes.some((route) =>
    currentPath.includes(route),
  );

  if (isPublicRoute) {
    if (!session?.params?.auth) {
      return false;
    }
  } else if (!session?.params?.auth || !session.currentCheckTime) {
    return false;
  }

  if (!authToken.current) {
    authToken.current = {
      token: session.params.auth.token,
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      generationDate: session.params.auth.tokenGenerationDate as Date,
    };
  }

  // make sure this is only called once at a time

  return navigator.locks.request("comprasSessionCheck", async () => {
    // If session is already marked as expired, don't attempt any further checks
    if (isSessionExpired()) {
      return false;
    }

    // fetch new access token if expired
    if (
      authToken.current &&
      session.params?.auth &&
      addSeconds(
        authToken.current.generationDate,
        // will be half of the expiresIn time so we can refresh the token before it expires
        Number(session.params.auth.expiresIn) / 2,
      ) < new Date()
    ) {
      return api
        .post<LoginResponse>("refreshToken", undefined, {
          noLoading: true,
          dontTriggerException: true,
          dontTriggerUnauthorizedException: true,
          headers: {
            "Refresh-Token": session.params.auth.refreshToken,
          },
        })
        .promise.then((response) => {
          if (response) {
            changeSession({
              ...response,
            });

            authToken.current = {
              token: response.auth.token,
              generationDate: new Date(),
            };

            return response.auth?.token;
          }

          // Refresh returned no valid response — onRequestResponse will
          // call setSessionExpired() when the 401 response is processed
          return false;
        })
        .catch(
          // Refresh token failed (e.g. 401) — onRequestResponse will call
          // setSessionExpired() when the 401 response is processed
          () => false,
        );
    }

    return authToken.current?.token || false;
  });
};
